// CheckboxSelection.js
import React, { useState, useMemo } from 'react';
import {
    Typography,
    TextField,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    Chip,
    FormHelperText,
    Divider
} from "@material-ui/core";

const CheckboxSelection = ({
    title,
    value = [],
    onChange,
    options,
    field,
    error,
    sortAlphabetically = true,
    displayProperty = null,
    valueProperty = null,
    height = '120px'
}) => {
    // Add search functionality
    const [searchTerm, setSearchTerm] = useState('');

    // Helper function to get display text
    const getDisplayText = (option) => {
        if (displayProperty && typeof option === 'object') {
            return option[displayProperty];
        }
        return option;
    };

    // Helper function to get value
    const getValue = (option) => {
        if (valueProperty && typeof option === 'object') {
            return option[valueProperty];
        }
        return option;
    };

    // Ensure value is always an array
    const safeValue = Array.isArray(value) ? value : [];

    // Memoize the filtered and sorted options to prevent recalculation on every render
    const { selectedOptions, unselectedOptions } = useMemo(() => {
        const selected = [];
        const unselected = [];

        // First, filter options based on search term
        let filteredOptions = [...options];
        if (searchTerm.trim()) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            filteredOptions = filteredOptions.filter(option => {
                const displayText = getDisplayText(option);
                return displayText.toLowerCase().includes(lowerSearchTerm);
            });
        }

        // Split into selected and unselected
        for (let i = 0; i < filteredOptions.length; i++) {
            const option = filteredOptions[i];
            const optionValue = getValue(option);
            if (safeValue.includes(optionValue)) {
                selected.push(option);
            } else {
                unselected.push(option);
            }
        }

        // Sort the unselected options alphabetically if needed
        if (sortAlphabetically) {
            unselected.sort((a, b) => {
                const aText = getDisplayText(a);
                const bText = getDisplayText(b);
                return typeof aText === 'string' ? aText.localeCompare(bText) : 0;
            });
        }

        return { selectedOptions: selected, unselectedOptions: unselected };
    }, [options, safeValue, searchTerm, sortAlphabetically, getDisplayText, getValue]);

    const handleToggle = (option) => {
        const optionValue = getValue(option);
        let newValues;

        if (safeValue.includes(optionValue)) {
            // Remove option if already selected
            newValues = safeValue.filter(val => val !== optionValue);
        } else {
            // Add option if not selected
            newValues = [...safeValue, optionValue];
        }

        // Don't sort the values when toggling - keep them in selection order
        onChange(field, newValues);
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    return (
        <div style={{ width: '100%' }}>
            {/* Selection Heading */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
                paddingBottom: '4px',
                borderBottom: '1px solid #e0e0e0'
            }}>
                <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    {safeValue.length} selected
                </Typography>
            </div>

            {/* Search Input */}
            <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={handleSearchChange}
                variant="outlined"
                fullWidth
                margin="dense"
                InputProps={{
                    startAdornment: (
                        <span style={{ marginRight: 8 }}>🔍</span>
                    ),
                    endAdornment: (
                        <span
                            style={{
                                cursor: 'pointer',
                                marginLeft: 8,
                                fontSize: '0.875rem',
                                color: '#757575',
                                opacity: searchTerm ? 1 : 0.5
                            }}
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering the TextField click
                                setSearchTerm('');
                            }}
                        >
                            ✕
                        </span>
                    ),
                    style: { fontSize: '0.875rem' }
                }}
                style={{ marginBottom: '8px' }}
            />

            {/* Scrollable Selection List */}
            <Paper
                style={{
                    height: height,
                    overflow: 'auto',
                    border: '1px solid #e0e0e0',
                    boxShadow: 'none'
                }}
            >
                <List dense component="div" role="list">
                    {/* SELECTED ITEMS SECTION */}
                    {selectedOptions.map((option) => (
                        <ListItem
                            key={`selected-${getValue(option)}`}
                            dense
                            button
                            onClick={() => handleToggle(option)}
                            style={{
                                padding: '2px 16px',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }}
                        >
                            <ListItemIcon style={{ minWidth: '32px' }}>
                                <Checkbox
                                    edge="start"
                                    checked={true}
                                    tabIndex={-1}
                                    disableRipple
                                    size="small"
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={getDisplayText(option)}
                                primaryTypographyProps={{
                                    style: {
                                        fontSize: '0.875rem',
                                        fontWeight: 500
                                    }
                                }}
                            />
                        </ListItem>
                    ))}

                    {/* Add a divider between selected and unselected */}
                    {selectedOptions.length > 0 && unselectedOptions.length > 0 && (
                        <Divider style={{ margin: '4px 0' }} />
                    )}

                    {/* UNSELECTED ITEMS SECTION - Show all items, not just 100 */}
                    {unselectedOptions.length > 0 ? (
                        unselectedOptions.map((option) => (
                            <ListItem
                                key={`unselected-${getValue(option)}`}
                                dense
                                button
                                onClick={() => handleToggle(option)}
                                style={{ padding: '2px 16px' }}
                            >
                                <ListItemIcon style={{ minWidth: '32px' }}>
                                    <Checkbox
                                        edge="start"
                                        checked={false}
                                        tabIndex={-1}
                                        disableRipple
                                        size="small"
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={getDisplayText(option)}
                                    primaryTypographyProps={{
                                        style: { fontSize: '0.875rem' }
                                    }}
                                />
                            </ListItem>
                        ))
                    ) : (
                        // Show this if there are no unselected items matching the search
                        selectedOptions.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary="No matches found"
                                    style={{ fontStyle: 'italic', color: 'gray' }}
                                />
                            </ListItem>
                        )
                    )}
                </List>
            </Paper>

            {/* Show selected items that are filtered out */}
            {searchTerm && safeValue.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                    <Typography variant="caption" color="textSecondary">
                        Selected items:
                    </Typography>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        marginTop: '4px'
                    }}>
                        {options
                            .filter(option => safeValue.includes(getValue(option)))
                            .map(item => (
                                <Chip
                                    key={getValue(item)}
                                    label={getDisplayText(item)}
                                    size="small"
                                    onDelete={() => handleToggle(item)}
                                    style={{ fontSize: '0.75rem' }}
                                />
                            ))}
                    </div>
                </div>
            )}

            {error && (
                <FormHelperText error>{error}</FormHelperText>
            )}
        </div>
    );
};

export default React.memo(CheckboxSelection);